// ─── Tipos: Plan de Manejo — Aplicaciones en Campo ───────────────────────────

export type CicloCosecha = 'PC' | 'RC' | 'RC2' | 'SEMILLERO' | 'BARBECHO';
export type EstadoBoleta = 'pendiente' | 'impresa' | 'aplicada' | 'cancelada';

// ─── Boom (equipo de aplicación) ─────────────────────────────────────────────
export interface AppBoom {
  id: number;
  empresa_id: number;
  codigo: string;          // "01", "02"
  nombre: string;          // "Boom Grande"
  capacidad_l: number;     // litros por llenado
  descripcion?: string;
  activo: boolean;
  created_at?: string;
}

// ─── Etapa del Plan de Manejo ─────────────────────────────────────────────────
export interface AppPlanEtapa {
  id: number;
  empresa_id: number;
  nombre: string;           // "Desarrollo PC", "Post Forza", "Desarrollo RC"
  ciclo_cosecha: CicloCosecha;
  orden: number;
  activo: boolean;
  created_at?: string;
  updated_at?: string;
  // join
  ciclos?: AppPlanCiclo[];
}

// ─── Ciclo de Aplicación ──────────────────────────────────────────────────────
export interface AppPlanCiclo {
  id: number;
  empresa_id: number;
  etapa_id: number;
  nombre: string;           // "Plag frut 1", "Foliar 19 pc"
  dias: number;             // días desde siembra
  boom_id?: number;
  agua_l?: number;          // litros de agua por boom
  grupo_quimico?: string;
  activo: boolean;
  created_at?: string;
  updated_at?: string;
  // joins
  etapa?: Pick<AppPlanEtapa, 'id' | 'nombre' | 'ciclo_cosecha'>;
  boom?: Pick<AppBoom, 'id' | 'codigo' | 'nombre' | 'capacidad_l'>;
  productos?: AppPlanProducto[];
}

// ─── Producto en Ciclo (fórmula base) ────────────────────────────────────────
export interface AppPlanProducto {
  id: number;
  empresa_id: number;
  ciclo_id: number;
  inv_producto_id?: number;  // FK inv_productos ERP
  articulo: string;          // código ERP
  nombre_producto: string;
  descripcion?: string;      // ingrediente activo
  grupo_quimico?: string;
  ingrediente_activo?: string;
  departamento?: string;
  dosis: number;             // por hectárea
  volumen?: number;          // litros de boom de referencia para la dosis
  medida?: string;           // "Kil", "Lt", "Caj", "Kg" — puede ser null en datos VFP
  latencia?: number;
  reingreso?: string;
  orden: number;
  activo: boolean;
  created_at?: string;
  updated_at?: string;
}

// ─── Boleta Semanal ───────────────────────────────────────────────────────────
export interface AppBoleta {
  id: number;
  empresa_id: number;
  numero: string;            // "0000001838"
  semana: string;            // "20-26"
  fecha_desde: string;
  fecha_hasta: string;
  // Territorio
  lote_id?: string;
  lote_num?: string;
  finca_nombre?: string;
  bloques?: string;
  // Agronómico
  fecha_siembra?: string;
  dias?: number;
  area_ha?: number;
  plantas?: number;
  densidad?: number;
  // Plan
  etapa_id?: number;
  etapa_nombre?: string;
  ciclo_id?: number;
  ciclo_nombre?: string;
  // Equipo
  boom_id?: number;
  boom_capacidad_l?: number;
  agua_l?: number;
  // Cálculos boom
  n_booms_completos?: number;   // INT(area × agua / capacidad_boom)
  litros_fraccion?: number;     // MOD(area × agua, capacidad_boom)
  agua_total_l?: number;        // agua × area
  // Control
  manual: boolean;
  estado: EstadoBoleta;
  fecha_plan?: string;
  fecha_aplicacion?: string;
  tratamiento?: string;
  usuario_id?: string;
  created_at?: string;
  updated_at?: string;
  // join
  productos?: AppBoletaProducto[];
}

// ─── Detalle de Producto en Boleta (cantidades calculadas) ───────────────────
export interface AppBoletaProducto {
  id: number;
  empresa_id: number;
  boleta_id: number;
  plan_producto_id?: number;
  inv_producto_id?: number | null;  // FK inv_productos ERP (enlace inventario)
  articulo: string;
  nombre_producto: string;
  descripcion?: string;
  justificacion?: string;
  ingrediente_activo?: string;
  grupo_quimico?: string;
  departamento?: string;
  medida?: string;
  reingreso?: string;
  latencia?: number;
  orden: number;
  // Fórmula base
  dosis?: number;
  volumen?: number;
  // Cantidades calculadas
  cantidad_aplicar?: number;    // dosis × area_ha
  cantidad_boom?: number;       // dosis / volumen × boom_capacidad
  cantidad_fraccion?: number;   // sobrante
  es_agua: boolean;
  created_at?: string;
}

// ─── Costos del Plan de Manejo (por hectárea) ────────────────────────────────
// Provienen de la vista v_app_plan_costos y la RPC app_costos_plan.
// El costo unitario es inv_productos.costo_promedio (costo prom. ponderado ERP).

export type MonedaCosto = 'CRC' | 'USD';

export interface CostoProductoLinea {
  producto_id: number;
  articulo: string;
  nombre_producto: string;
  departamento?: string | null;
  dosis: number;
  medida?: string | null;
  costo_unitario: number;     // ₡ por unidad de medida (costo en CRC, convertido si origen USD)
  costo_ha: number;           // dosis × costo_unitario (CRC)
  // Multimoneda: moneda en que fue ingresado el costo + equivalente USD
  moneda_costo?: MonedaCosto;       // moneda ORIGINAL del costo del producto
  tipo_cambio_costo?: number;       // TC usado si origen USD (1 si CRC)
  costo_unitario_orig?: number;     // costo unitario en su moneda original
  costo_unitario_usd?: number;      // equivalente USD del costo unitario
  costo_ha_usd?: number;            // dosis × costo en USD
  sin_costo: boolean;         // sin enlace a inventario o costo 0
  en_bodega_finca: boolean;   // tiene saldo en bodega Finca
}

export interface CostoCiclo {
  ciclo_id: number;
  ciclo: string;
  dias: number;
  costo_ha: number;
  costo_ha_usd?: number;
  n_productos: number;
  n_sin_costo: number;
  productos: CostoProductoLinea[];
}

export interface CostoEtapa {
  etapa_id: number;
  etapa: string;
  ciclo_cosecha: CicloCosecha;
  costo_ha: number;
  costo_ha_usd?: number;
  n_sin_costo: number;
  ciclos: CostoCiclo[];
}

export interface CostoDepartamento {
  departamento: string;
  costo_ha: number;
}

export interface CostosPlan {
  empresa_id: number;
  ciclo_cosecha: CicloCosecha | null;
  costo_ha_total: number;
  costo_ha_total_usd?: number;
  n_sin_costo: number;
  etapas: CostoEtapa[];
  por_departamento: CostoDepartamento[];
}

// ─── Fórmulas de cálculo (replicadas de VFP) ─────────────────────────────────

export interface CalculoBoleta {
  n_booms_completos: number;
  litros_fraccion: number;
  agua_total_l: number;
}

export interface CalculoProducto {
  cantidad_aplicar: number;   // dosis × area_ha
  cantidad_boom: number;      // dosis / volumen × boom_capacidad
  cantidad_fraccion: number;  // MOD(dosis × area, cantidad_boom)
}

export function calcularBoleta(
  area_ha: number,
  agua_l: number,
  boom_capacidad_l: number
): CalculoBoleta {
  const agua_total_l = agua_l * area_ha;
  const n_booms_completos = Math.floor(agua_total_l / boom_capacidad_l);
  const litros_fraccion = agua_total_l % boom_capacidad_l;
  return { n_booms_completos, litros_fraccion, agua_total_l };
}

export function calcularProducto(
  dosis: number,
  volumen: number,
  area_ha: number,
  boom_capacidad_l: number
): CalculoProducto {
  const cantidad_aplicar = dosis * area_ha;
  const cantidad_boom = volumen > 0 ? (dosis / volumen) * boom_capacidad_l : 0;
  const cantidad_fraccion = cantidad_boom > 0 ? cantidad_aplicar % cantidad_boom : 0;
  return { cantidad_aplicar, cantidad_boom, cantidad_fraccion };
}

export function calcularAguaRequerida(
  agua_l: number,
  area_ha: number,
  boom_capacidad_l: number
): AppBoletaProducto {
  const agua_total = agua_l * area_ha;
  const boom_agua = agua_total < boom_capacidad_l ? 0 : boom_capacidad_l;
  const fraccion_agua = agua_total % boom_capacidad_l;
  return {
    id: 0,
    empresa_id: 0,
    boleta_id: 0,
    articulo: '999',
    nombre_producto: 'AGUA REQUERIDA',
    descripcion: `Volumen de fórmula ${agua_l} Lts.`,
    medida: 'LT',
    orden: 9999,
    dosis: agua_l,
    volumen: agua_l,
    cantidad_aplicar: agua_total,
    cantidad_boom: boom_agua,
    cantidad_fraccion: fraccion_agua,
    es_agua: true,
  };
}

// ─── Helper: número de semana CR ─────────────────────────────────────────────
export function semanaAnio(fecha: Date): string {
  const start = new Date(fecha.getFullYear(), 0, 1);
  const diff = fecha.getTime() - start.getTime();
  const week = Math.ceil((diff / 86_400_000 + start.getDay() + 1) / 7);
  return `${String(week).padStart(2, '0')}-${fecha.getFullYear()}`;
}

export function lunesDeSemana(semanaStr: string): Date {
  const [w, y] = semanaStr.split('-').map(Number);
  const jan1 = new Date(y, 0, 1);
  const days = (w - 1) * 7 - jan1.getDay() + 1;
  const lunes = new Date(y, 0, 1 + days);
  return lunes;
}

export function sabadoDeSemana(lunes: Date): Date {
  const s = new Date(lunes);
  s.setDate(s.getDate() + 5);
  return s;
}
